<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
          </div>
          <h5 class="modal-title">{{ mode === 'module' ? 'Assign Ad-Hoc Module' : 'Assign Users to Program' }}</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body p-0">

      <div class="search-area p-3 border-bottom">
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Search by name or email..."
            autofocus
            :disabled="loading"
          />
        </div>
      </div>

      <div class="module-list-container">

        <div v-if="loading" class="text-center p-5">
          <div class="spinner-border text-info" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-2 small">Fetching users...</p>
        </div>

        <div v-else-if="filteredUsers.length === 0" class="text-center text-muted p-4">
          No users found.
        </div>

        <template v-else>
         <div class="module-selection-item select-all-row pointer" @click="toggleSelectAll">
            <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-3">
              <input
                class="form-check-input m-0"
                type="checkbox"
                :checked="isAllFilteredSelected"
                @click.stop="toggleSelectAll"
              >
              <span class="fw-bold text-dark" style="font-size: 0.95rem;">Select All {{ searchQuery ? 'Filtered' : '' }} Users</span>
            </div>
          </div>

          <div
            v-for="user in filteredUsers"
            :key="user.id"
            class="module-selection-item"
            @click="toggleSelection(user.id)"
          >
            <div class="form-check custom-checkbox m-0 p-0 d-flex align-items-center gap-3 pointer">
              <input
                class="form-check-input m-0"
                type="checkbox"
                :value="user.id"
                v-model="selectedUserIds"
                @click.stop
              >
              <div class="user-avatar-sm">
                {{ user.first_name ? user.first_name.charAt(0) : '' }}{{ user.last_name ? user.last_name.charAt(0) : '' }}
              </div>
              <div class="d-flex flex-column">
                <span class="fw-bold text-dark" style="font-size: 0.95rem;">{{ user.first_name }} {{ user.last_name }}</span>
                <span class="text-muted small">{{ user.email || 'No email provided' }}</span>
              </div>
            </div>
          </div>
        </template>

      </div>
    </div>

    <div class="modal-footer" :class="{'bg-warning-soft': confirmState}">

      <template v-if="!confirmState">
        <div class="me-auto text-muted small fw-bold">
          {{ isAllGloballySelected ? 'All' : selectedUserIds.length }} selected
        </div>
        <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="handleSaveClick"
        >
        Save
        </button>
      </template>

      <template v-else>
        <div class="me-auto d-flex align-items-center text-dark small fw-bold">
          <i class="fas fa-exclamation-circle text-warning me-2 fs-5"></i>
          {{ confirmMessage }}
        </div>
        <button type="button" class="btn btn-secondary bg-white" @click="confirmState = false">No, Cancel</button>
        <button
          type="button"
          class="btn btn-warning fw-bold text-dark border-0 shadow-sm"
          :disabled="loading"
          @click="executeSave"
        >
          Yes, Proceed
        </button>
      </template>

    </div>
  </vue-final-modal>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, defineEmits, defineProps } from 'vue';

const props = defineProps(['modelValue', 'orgId', 'programId', 'moduleId', 'mode', 'isAllType']);
const emit = defineEmits(['update:modelValue', 'save']);

const { proxy } = getCurrentInstance();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const searchQuery = ref('');
const selectedUserIds = ref([]);
const initialAssignedIds = ref([]);
const users = ref([]);
const loading = ref(false);

const confirmState = ref(false);
const confirmMessage = ref('');

watch(() => props.modelValue, async (newVal) => {
  if (newVal && props.orgId) {
    searchQuery.value = '';
    selectedUserIds.value = [];
    initialAssignedIds.value = [];

    confirmState.value = false;

    await fetchUsersAndEnrollments();
  }
});

const fetchUsersAndEnrollments = async () => {
  loading.value = true;
  try {
    const userRes = await proxy.$axios.get(`/users/organisations/${props.orgId}`);
    if (userRes.data.success) {
      users.value = userRes.data.users || [];
    }

if (props.mode === 'program' && props.programId) {
      if (props.isAllType) {
        const ids = users.value.map(u => u.id);
        selectedUserIds.value = [...ids];
        initialAssignedIds.value = [...ids];
     } else {
        const enrollRes = await proxy.$axios.get(`/orgadmin/trainings/organisations/${props.orgId}/programs/${props.programId}/enrollments`);
        if (enrollRes.data.success && enrollRes.data.enrollments) {
          const activeUserIds = users.value.map(u => u.id);

          const uniqueIds = [...new Set(enrollRes.data.enrollments.map(e => e.user_id || e.UserID))]
                              .filter(id => activeUserIds.includes(id));

          selectedUserIds.value = [...uniqueIds];
          initialAssignedIds.value = [...uniqueIds];
        }
      }
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    proxy.$toast.error("Failed to load user data.");
  } finally {
    loading.value = false;
  }
};

const filteredUsers = computed(() => {
  let filtered = users.value.filter(user => {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const query = searchQuery.value.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  return filtered.sort((a, b) => {
    const aAssigned = initialAssignedIds.value.includes(a.id);
    const bAssigned = initialAssignedIds.value.includes(b.id);

    if (aAssigned && !bAssigned) return -1;
    if (!aAssigned && bAssigned) return 1;

    const nameA = (a.first_name || '').toLowerCase();
    const nameB = (b.first_name || '').toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
});

// Controls the UI Checkbox (True if all VISIBLE users are checked)
const isAllFilteredSelected = computed(() => {
  return filteredUsers.value.length > 0 && filteredUsers.value.every(u => selectedUserIds.value.includes(u.id));
});

// Controls the Backend Payload (True if ALL users in the org are checked)
const isAllGloballySelected = computed(() => {
  return users.value.length > 0 && selectedUserIds.value.length === users.value.length;
});

const toggleSelectAll = () => {
  if (isAllFilteredSelected.value) {
    // Uncheck currently filtered items
    const filteredIds = filteredUsers.value.map(u => u.id);
    selectedUserIds.value = selectedUserIds.value.filter(id => !filteredIds.includes(id));
  } else {
    // Check currently filtered items
    const filteredIds = filteredUsers.value.map(u => u.id);
    const newSelections = filteredIds.filter(id => !selectedUserIds.value.includes(id));
    selectedUserIds.value.push(...newSelections);
  }
};

const toggleSelection = (id) => {
  const index = selectedUserIds.value.indexOf(id);
  if (index === -1) {
    selectedUserIds.value.push(id);
  } else {
    selectedUserIds.value.splice(index, 1);
  }
};

const closeModal = () => {
  isOpen.value = false;
  confirmState.value = false;
};

const handleSaveClick = () => {
  if (isAllGloballySelected.value) {
    confirmMessage.value = "Deploying to ALL users in the organisation. Continue?";
    confirmState.value = true;
  } else if (selectedUserIds.value.length === 0) {
    confirmMessage.value = "Unassigning ALL users from this training. Continue?";
    confirmState.value = true;
  } else {
    executeSave();
  }
};

const executeSave = () => {
  const selectedUsers = users.value.filter(u => selectedUserIds.value.includes(u.id));
  emit('save', selectedUsers, props.programId, props.moduleId, props.mode, isAllGloballySelected.value);
  confirmState.value = false;
};
</script>

<style scoped>
:deep(.modal-overlay-container) { display: flex; justify-content: center; align-items: center; background-color: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); z-index: 1000; }
:deep(.modern-modal-content) { background: white; border-radius: 12px; width: 100%; max-width: 550px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column; border: none !important; padding: 0; margin: 0 1rem; overflow: hidden; max-height: 85vh; }

.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid #f1f5f9; background-color: #ffffff; }
.title-wrapper { display: flex; align-items: center; gap: 0.75rem; }
.title-icon { color: #00a0c1; display: flex; align-items: center; justify-content: center; background: rgba(0, 160, 193, 0.1); padding: 8px; border-radius: 8px; }
.modal-title { color: #1e293b; margin: 0; font-size: 1.15rem; font-weight: 700; letter-spacing: -0.01em; }
.close-button { background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #94a3b8; transition: all 0.2s; }
.close-button:hover { background-color: #fee2e2; color: #dc3545; transform: rotate(90deg); }

.search-area { background-color: #f8fafc; }
.input-with-icon { position: relative; display: flex; align-items: center; }
.input-icon { position: absolute; left: 12px; color: #94a3b8; z-index: 1; }
.form-control { width: 100%; padding: 0.6rem 0.6rem 0.6rem 2.2rem; border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 0.95rem; transition: all 0.2s; }
.form-control:focus { border-color: #00a0c1; box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15); }

.module-list-container { overflow-y: auto; max-height: 350px; }

/* Selection Items */
.module-selection-item { padding: 1rem 1.5rem; border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: background-color 0.15s; }
.module-selection-item:hover { background-color: #f8fafc; }
.module-selection-item:last-child { border-bottom: none; }
.select-all-row { background-color: #f8fafc; border-bottom: 2px solid #e2e8f0; }
.pointer { cursor: pointer; }

/* Custom Checkbox & Avatar */
.custom-checkbox .form-check-input { width: 1.25rem; height: 1.25rem; border: 1.5px solid #cbd5e1; cursor: pointer; }
.custom-checkbox .form-check-input:checked { background-color: #00a0c1; border-color: #00a0c1; }
.user-avatar-sm { width: 36px; height: 36px; border-radius: 50%; background: #e0e7ff; color: #00a0c1; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.85rem; }

/* Footer */
.modal-footer { display: flex; align-items: center; justify-content: flex-end; border-top: 1px solid #f1f5f9; padding: 1rem 1.5rem; gap: 0.75rem; background-color: #ffffff; }
.btn { padding: 0.5rem 1.25rem; border-radius: 8px; font-size: 0.9rem; font-weight: 600; transition: all 0.2s; }
.btn-primary { background-color: #00a0c1; color: white; border: none; }
.btn-primary:hover:not(:disabled) { background-color: #008da8; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary { background-color: white; color: #64748b; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background-color: #f8fafc; color: #1e293b; }

.modal-footer {
  transition: background-color 0.3s ease;
}
.bg-warning-soft {
  background-color: #fffbeb !important;
  border-top-color: #fde68a !important;
}
</style>